import { useEffect, useState } from 'react';
import './ColorField.css';

const HEX_FULL = /^#[0-9A-F]{6}$/;
const HEX_SHORT = /^#[0-9A-F]{3}$/;

// Cores sugeridas, para quem não quer abrir o seletor nem conhece o código da marca.
const DEFAULT_PRESETS = [
    '#FB6A21', '#E74C3C', '#C0392B', '#E91E63', '#9B59B6',
    '#3F51B5', '#1E6FD9', '#0B2A6F', '#00A3A3', '#2E9E5B', '#F1B400', '#2D3748'
];

/** Deixa o texto digitado no formato #RRGGBB em maiúsculas, sem aceitar o que não é hexadecimal. */
const sanitize = (text) => {
    const digits = (text || '').replace(/[^0-9a-fA-F]/g, '').slice(0, 6).toUpperCase();
    return digits ? `#${digits}` : '';
};

/** "#ABC" vira "#AABBCC": o servidor só aceita a forma completa. */
const expand = (hex) => (HEX_SHORT.test(hex)
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex);

/**
 * Escolha de cor: amostra grande que abre o seletor do sistema, código hexadecimal editável e
 * uma paleta de sugestões. Mesmo desenho do seletor de cor da Identidade visual.
 *
 * Só entrega ao formulário um valor completo (#RRGGBB): enquanto a pessoa digita, o texto
 * parcial fica no campo e o valor anterior continua valendo.
 *
 * @param {string} value - Cor atual (#RRGGBB) ou vazio.
 * @param {function} onChange - Recebe a nova cor.
 * @param {string} fallback - Cor mostrada na amostra quando não há valor.
 * @param {string[]} presets - Cores sugeridas.
 * @param {function} onClear - Quando informada e há cor escolhida, mostra o botão de voltar ao padrão.
 */
const ColorField = ({ value, onChange, onClear, clearLabel = 'Usar padrão', fallback = '#FB6A21', presets = DEFAULT_PRESETS, label = 'Cor' }) => {
    const [draft, setDraft] = useState(value || '');

    // Acompanha mudanças vindas de fora (carga da edição, escolha na paleta).
    useEffect(() => {
        setDraft(value || '');
    }, [value]);

    const current = (value || '').toUpperCase();

    const handleType = (event) => {
        const next = sanitize(event.target.value);
        setDraft(next);
        if (HEX_FULL.test(next)) {
            onChange(next);
        }
    };

    // Ao sair do campo, completa o atalho de 3 dígitos ou volta ao último valor válido.
    const handleBlur = () => {
        const expanded = expand(draft);
        if (HEX_FULL.test(expanded)) {
            setDraft(expanded);
            onChange(expanded);
        } else {
            setDraft(value || '');
        }
    };

    return (
        <div className="color-field">
            <div className="color-field__main">
                <div className="color-field__swatch" style={{ '--color-field': current || fallback }}>
                    <input
                        type="color"
                        className="color-field__native"
                        value={(current || fallback).toLowerCase()}
                        onChange={(event) => onChange(event.target.value.toUpperCase())}
                        aria-label={`Escolher ${label.toLowerCase()}`}
                        title={`Escolher ${label.toLowerCase()}`}
                    />
                    <i className="fa-solid fa-eyedropper" aria-hidden="true"></i>
                </div>
                <div className="color-field__code">
                    <small>Código hexadecimal</small>
                    <input
                        type="text"
                        className="form-control input-default color-field__hex"
                        placeholder={fallback}
                        value={draft}
                        onChange={handleType}
                        onBlur={handleBlur}
                        maxLength={7}
                        spellCheck={false}
                        aria-label="Código hexadecimal da cor"
                    />
                </div>
                {onClear && value && (
                    <button type="button" className="btn btn-outline-secondary color-field__clear" onClick={onClear}
                        title={clearLabel}>
                        <i className="fa-solid fa-rotate-left me-2" aria-hidden="true"></i>{clearLabel}
                    </button>
                )}
            </div>

            {presets.length > 0 && (
                <div className="color-field__presets" role="group" aria-label="Cores sugeridas">
                    {presets.map((preset) => (
                        <button
                            type="button"
                            key={preset}
                            className={`color-field__preset ${current === preset.toUpperCase() ? 'is-active' : ''}`}
                            style={{ background: preset }}
                            onClick={() => onChange(preset.toUpperCase())}
                            aria-label={`Usar a cor ${preset}`}
                            aria-pressed={current === preset.toUpperCase()}
                            title={preset}
                        >
                            {current === preset.toUpperCase() && <i className="fa-solid fa-check" aria-hidden="true"></i>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ColorField;
